import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";

export interface InfiniteMeetingsResult<T> {
  meetings: T[];
  loadMorePast: () => Promise<void>;
  loadMoreFuture: () => Promise<void>;
  loadingPast: boolean;
  loadingFuture: boolean;
  reachedPast: boolean;
  reachedFuture: boolean;
  initialLoading: boolean;
  initialLoadFailed: boolean;
}

export function useInfiniteMeetings<T extends { id: number; start: string }>(
  fetchFn: (start: string, until: string) => Promise<T[]>,
  windowMonths = 2
): InfiniteMeetingsResult<T> {
  const [meetings, setMeetings] = useState<T[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [loadingFuture, setLoadingFuture] = useState(false);
  const [reachedPast, setReachedPast] = useState(false);
  const [reachedFuture, setReachedFuture] = useState(false);

  const pastBoundaryRef = useRef<moment.Moment>(moment());
  const futureBoundaryRef = useRef<moment.Moment>(moment());
  const loadingPastRef = useRef(false);
  const loadingFutureRef = useRef(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const start = moment().subtract(windowMonths, "months");
    const until = moment().add(windowMonths, "months");
    pastBoundaryRef.current = start;
    futureBoundaryRef.current = until;
    fetchFn(start.toISOString(), until.toISOString())
      .then((m) => {
        setMeetings(m);
        setInitialLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setInitialLoadFailed(true);
        setInitialLoading(false);
      });
  }, []);

  const loadMorePast = useCallback(async () => {
    if (loadingPastRef.current || reachedPast || initialLoading) return;
    loadingPastRef.current = true;
    setLoadingPast(true);
    const newStart = pastBoundaryRef.current
      .clone()
      .subtract(windowMonths, "months");
    const until = pastBoundaryRef.current.clone();
    try {
      const m = await fetchFn(newStart.toISOString(), until.toISOString());
      pastBoundaryRef.current = newStart;
      if (m.length === 0) {
        setReachedPast(true);
      } else {
        setMeetings((prev) => {
          const existing = new Set(prev.map((p) => p.id));
          return [...prev, ...m.filter((x) => !existing.has(x.id))];
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      loadingPastRef.current = false;
      setLoadingPast(false);
    }
  }, [fetchFn, reachedPast, initialLoading, windowMonths]);

  const loadMoreFuture = useCallback(async () => {
    if (loadingFutureRef.current || reachedFuture || initialLoading) return;
    loadingFutureRef.current = true;
    setLoadingFuture(true);
    const start = futureBoundaryRef.current.clone();
    const newUntil = futureBoundaryRef.current
      .clone()
      .add(windowMonths, "months");
    try {
      const m = await fetchFn(start.toISOString(), newUntil.toISOString());
      futureBoundaryRef.current = newUntil;
      if (m.length === 0) {
        setReachedFuture(true);
      } else {
        setMeetings((prev) => {
          const existing = new Set(prev.map((p) => p.id));
          return [...prev, ...m.filter((x) => !existing.has(x.id))];
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      loadingFutureRef.current = false;
      setLoadingFuture(false);
    }
  }, [fetchFn, reachedFuture, initialLoading, windowMonths]);

  return {
    meetings,
    loadMorePast,
    loadMoreFuture,
    loadingPast,
    loadingFuture,
    reachedPast,
    reachedFuture,
    initialLoading,
    initialLoadFailed,
  };
}
